import React, { useState, useEffect } from "react";
import { TabPane, Row, Col } from "reactstrap";
import "./index.scss";
import CustomTextInput from "../../../../components/CustomTextInput";
import CustomDropdownInput from "../../../../components/CustomDropdownInput";
import _ from "lodash";
import t from "utils/Lang";
import ModalCreateContract from "components/ModalCreateContract";
import ApiCaller from "api/apiCaller";
import { getContractLocation } from "utils/SiteHelper";

interface StoreInformationTabProps {
  isView: boolean;
  storeData: any;
  setStoreData: Function;
  statusOptions: any;
  customerGroupOptions: any;
  storeFormatOptions: any;
  storeTypeOptions: any;
  parkingSiteTypeOptions: any;
  financial: any;
}

export default function StoreInformationTab({
  isView,
  storeData,
  setStoreData,
  statusOptions,
  customerGroupOptions,
  storeFormatOptions,
  storeTypeOptions,
  parkingSiteTypeOptions,
  financial,
}: StoreInformationTabProps) {
  const [customAttributes, setCustomAttributes] = useState<Array<any>>([]);
  const [isOpenCreateContract, setOpenContract] = useState(false);
  const toggle = () => setOpenContract(!isOpenCreateContract);
  useEffect(() => {
    let data: Array<any> = [];
    const chunkCustomAttributes = _.chunk(storeData?.customAttributes, 2);
    chunkCustomAttributes.forEach((array) => {
      array.forEach((item) => {
        data.push(item);
      });
    });
    setCustomAttributes(data);
  }, []);

  const callbackCustomData = (attribute: string, value: any) => {
    if (!value) return;
    let customAttributeData: any = {};
    if (
      storeData?.data?.custom_attribute_data &&
      storeData?.data?.custom_attribute_data !== ""
    ) {
      customAttributeData = JSON.parse(storeData?.data?.custom_attribute_data);
    }
    customAttributeData[attribute] = value.value || value;
    setStoreData({
      ...storeData,
      data: {
        ...storeData?.data,
        custom_attribute_data: JSON.stringify(customAttributeData),
      },
    });
  };

  const updateAttr = (attribute: string, value: any) => {
    if (attribute === "status_expire") {
      setStoreData({
        ...storeData,
        data: {
          ...storeData?.data,
          contract: {
            ...storeData?.data?.contract,
            should_renew: value?.value === 1,
          },
        },
      });
      return;
    }
    let dataUpdate = {};
    if (value?.label) {
      // Check value is object (value from dropdown menu)
      dataUpdate = {
        [attribute]: {
          id: value?.value,
          name: value?.label,
        },
      };
    } else {
      dataUpdate = { [attribute]: value };
    }
    setStoreData({
      ...storeData,
      ...{ data: { ...storeData?.data, ...dataUpdate } },
    });
  };

  const uiLayoutDefinition: any = [
    {
      id: "contract",
      type: "row",
      label: t().contract,
      items: storeData.data?.contract
        ? [
            {
              attributeName: "available_from",
              type: "text",
              size: 6,
              value: storeData.data?.contract?.available_from,
              label: t().store_listing__contract_from,
              isView: true,
            },
            {
              attributeName: "available_to",
              type: "text",
              size: 6,
              value: storeData.data?.contract?.available_to,
              label: t().store_listing__contract_to,
              isView: true,
            },
            {
              attributeName: "rental_fee",
              type: "text",
              size: 6,
              value: storeData.data?.payment_terms?.[0]?.rental_fee,
              label: t().current_rental_fee,
              isNumeric: true,
              isView: true,
            },
            {
              attributeName: "tax",
              type: "text",
              size: 6,
              value:
                (storeData.data?.payment_terms?.[0]?.tax_percentage || 0) * 100,
              label: `${t().contract_tax} (%)`,
              isNumeric: true,
              isView: true,
            },
            {
              attributeName: "rental_fee_with_tax",
              type: "text",
              size: 6,
              value: storeData.data?.payment_terms?.[0]?.rental_fee_with_tax,
              label: t().current_rental_fee_with_tax,
              isNumeric: true,
              isView: true,
            },
            {
              attributeName: "status_expire",
              type: "dropdown",
              size: 6,
              label: t().renew_status,
              options: [
                { value: 1, label: "Đồng ý gia hạn" },
                { value: 2, label: "Không gia hạn" },
              ],
              value:
                storeData.data?.contract?.should_renew === true
                  ? 1
                  : storeData.data?.contract?.should_renew === false
                  ? 2
                  : 0,
            },
          ]
        : [],
    },
  ];
  if (financial) {
    uiLayoutDefinition.push({
      id: "financial",
      type: "row",
      label: t().financial_performance,
      items: [
        {
          attributeName: "rev_current",
          type: "text",
          size: 6,
          label: t().revenue_current,
          value: financial?.revenue?.detail?.[0]?.value,
          isNumeric: true,
          isView: true,
        },
        {
          attributeName: "rev_12",
          type: "text",
          size: 6,
          label: t().revenue_last_12,
          value: financial?.revenue?.detail
            ? financial?.revenue?.detail.reduce(
                (res: number, val: any) => res + val.value,
                0
              )
            : 0,
          isNumeric: true,
          isView: true,
        },
        ...financial?.revenue?.years?.map?.((el: any) => ({
          attributeName: `rev_${el.year}`,
          type: "text",
          size: 6,
          label: `${t().revenue} (${el.year})`,
          value: el.value,
          isNumeric: true,
          isView: true,
        })),
        {
          attributeName: "ebitda_current",
          type: "text",
          size: 6,
          label: t().ebitda_current,
          value: financial?.ebitda?.detail?.[0]?.value,
          isNumeric: true,
          isView: true,
        },
        {
          attributeName: "ebitda_12",
          type: "text",
          size: 6,
          label: t().ebitda_last_12,
          value: financial?.ebitda?.detail
            ? financial?.ebitda?.detail.reduce(
                (res: number, val: any) => res + val.value,
                0
              )
            : 0,
          isView: true,
        },
        ...financial?.ebitda?.years?.map?.((el: any) => ({
          attributeName: `ebitda_${el.year}`,
          type: "text",
          size: 6,
          label: `${t().ebitda} (${el.year})`,
          value: el.value,
          isNumeric: true,
          isView: true,
        })),
      ],
    });
  }
  const onApply = async (data: any, paymentTerms: Array<any>, taxData: any) => {
    const contract_tax: any = {
      valid_from: data.available_from,
      valid_to: data.available_to,
      tax_percentage: taxData.tax_percentage / 100,
      contract_tax_type_id: taxData.contract_tax_type_id,
    };
    const paymentTermsBody = paymentTerms.map((el, index) => {
      if (index === 0) {
        el.increase_fee_rate = null;
      }
      return el;
    });
    const res = await ApiCaller.put(`contract/${data.id}`, data);
    await ApiCaller.post(`contract/${res.result.id}/contract_tax`, {
      contract_taxes: [contract_tax],
    });
    await ApiCaller.post(`contract/${data.id}/payment_term`, {
      payment_terms: paymentTermsBody,
    });
    window.location.reload();
  };
  return (
    <>
      <TabPane tabId="store_information_tab" style={{}}>
        {uiLayoutDefinition.map((row: any, index: number) => {
          const isContract = row.id === "contract";
          const canViewContract = isContract && row.items.length > 0;
          const emptyContract = isContract && row.items.length === 0;
          return (
            <div key={row.id}>
              <div
                className={`store-summary__label-title ${
                  canViewContract && "normal-button"
                }`}
                onClick={canViewContract ? toggle : undefined}
              >
                <span>{row.label}</span>
                {canViewContract && (
                  <i
                    style={{ marginLeft: 5 }}
                    className="material-icons-outlined"
                  >
                    description
                  </i>
                )}
              </div>
              <Row className="store-information-tab__section">
                {emptyContract ? (
                  <Col sm={{ size: 12, offset: 0 }}>
                    <div
                      className="empty-contract"
                      dangerouslySetInnerHTML={{
                        __html: t().empty_contract(storeData?.data?.site?.id),
                      }}
                    />
                  </Col>
                ) : (
                  row.items.map((layoutItem: any, layoutIndex: number) => {
                    let customAttributeData: any = {};
                    if (
                      storeData?.data?.custom_attribute_data &&
                      storeData?.data?.custom_attribute_data !== ""
                    ) {
                      customAttributeData = JSON.parse(
                        storeData?.data?.custom_attribute_data
                      );
                    }
                    if (layoutItem.type === "dropdown") {
                      return (
                        <Col
                          key={`layout-${index}-${layoutIndex}`}
                          sm={{ size: layoutItem.size, offset: 0 }}
                        >
                          <CustomDropdownInput
                            disabled={layoutItem.isView || isView}
                            style={styles.customTextInput}
                            callback={updateAttr}
                            data={storeData?.data}
                            options={layoutItem.options}
                            labels={storeData?.labels}
                            attributeName={layoutItem.attributeName}
                            customValue={layoutItem.value}
                            labelText={layoutItem.label}
                          />
                        </Col>
                      );
                    } else if (
                      layoutItem.type === "text" ||
                      layoutItem.type === "textarea" ||
                      layoutItem.type === "date"
                    ) {
                      return (
                        <Col
                          key={`layout-index-${layoutIndex}`}
                          sm={{ size: layoutItem.size, offset: 0 }}
                        >
                          <CustomTextInput
                            type={layoutItem.type}
                            isNumeric={layoutItem.isNumeric || false}
                            isDecimal={layoutItem.isDecimal || false}
                            disabled={layoutItem.isView || isView}
                            style={styles.customTextInput}
                            callback={updateAttr}
                            data={storeData?.data}
                            labels={storeData?.labels}
                            attributeName={layoutItem.attributeName}
                            inputBoxRows={layoutItem.inputBoxRows}
                            value={layoutItem.value}
                            labelText={layoutItem.label}
                          />
                        </Col>
                      );
                    } else if (layoutItem.type === "break") {
                      return (
                        <Col
                          key={`layout-index-${layoutIndex}`}
                          sm={{ size: 6, offset: 0 }}
                        />
                      );
                    } else if (layoutItem.type === "custom") {
                      return (
                        <Col
                          key={`layout-index-${layoutIndex}`}
                          sm={{ size: layoutItem.size, offset: 0 }}
                        >
                          {layoutItem.component}
                        </Col>
                      );
                    } else if (layoutItem.type === "select") {
                      return (
                        <Col
                          key={`layout-${index}-${layoutIndex}`}
                          sm={{ size: 6, offset: 0 }}
                        >
                          <CustomDropdownInput
                            disabled={layoutItem.isView || isView}
                            style={styles.customTextInput}
                            callback={callbackCustomData}
                            data={storeData?.data}
                            options={
                              customAttributeOptions[layoutItem.code_name]
                            }
                            attributeName={layoutItem.code_name}
                            labelText={layoutItem.name}
                            customValue={
                              customAttributeData[layoutItem.code_name]
                            }
                          />
                        </Col>
                      );
                    } else {
                      return (
                        <Col
                          key={`layout-index-${layoutIndex}`}
                          sm={{ size: 6, offset: 0 }}
                        >
                          <CustomTextInput
                            type="text"
                            isNumeric={layoutItem.isNumeric}
                            isDecimal={layoutItem.isDecimal}
                            disabled={layoutItem.isView || isView}
                            style={styles.customTextInput}
                            callback={callbackCustomData}
                            value={customAttributeData[layoutItem.code_name]}
                            labelText={layoutItem.name}
                            attributeName={layoutItem.code_name}
                            inputBoxRows={layoutItem.inputBoxRows}
                          />
                        </Col>
                      );
                    }
                  })
                )}
              </Row>
            </div>
          );
        })}
      </TabPane>
      <ModalCreateContract
        siteAddress={getContractLocation(storeData.data)}
        onClosed={() => {}}
        isOpen={isOpenCreateContract}
        toggle={toggle}
        onApply={onApply}
        contractId={storeData.data?.contract?.id}
        siteId={storeData?.data?.site?.id}
      />
    </>
  );
}

const styles = {
  customTextInput: {
    marginTop: 20,
  },
};

const customAttributeOptions: any = {
  main_road_ways: [
    {
      label: "Một chiều",
      value: "Một chiều",
    },
    {
      label: "Hai chiều có thanh chắn",
      value: "Hai chiều có thanh chắn",
    },
    {
      label: "Hai chiều không có thanh chắn",
      value: "Hai chiều không có thanh chắn",
    },
  ],
  is_flooded_with_tide: [
    {
      label: "Có",
      value: "Có",
    },
    {
      label: "Không",
      value: "Không",
    },
  ],
  is_flooded_with_rain: [
    {
      label: "Có",
      value: "Có",
    },
    {
      label: "Không",
      value: "Không",
    },
  ],
  wc_location: [
    {
      label: "WC trong toà nhà",
      value: "WC trong toà nhà",
    },
    {
      label: "WC của mặt bằng",
      value: "WC của mặt bằng",
    },
  ],
};
